import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { errMsg } from '../../lib/aiInterviewApi';
import { RECS, roomAPI } from '../../lib/roomApi';
import { Pill } from '../aiinterview/recruiter/common';
import { CardView, PanelStatus, VerdictCard } from './ScorecardParts';

const toForm = (mine) => ({ ratings: mine?.ratings || {}, notes: mine?.notes || '', recommendation: mine?.recommendation || null });
const toBody = (f) => ({ ratings: Object.entries(f.ratings).map(([competency, r]) => ({ competency, score: r.score ?? null, note: r.note || '' })),
  notes: f.notes, recommendation: f.recommendation });

const ScoreRow = ({ c, i, r, onChange }) => (
  <div className="vr-score-row" data-testid={`scorecard-row-${i}`}>
    <div className="iv-row" style={{ justifyContent: 'space-between' }}>
      <b style={{ color: '#002A38', fontSize: '0.9rem' }}>{c.name} {c.must_have && <Pill tone="navy">Must-have</Pill>}</b>
      <div className="iv-row" style={{ gap: '0.25rem' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} className={`vr-score ${r.score === n ? 'is-on' : ''}`} onClick={() => onChange({ ...r, score: r.score === n ? null : n })} data-testid={`scorecard-score-${i}-${n}`}>{n}</button>
        ))}
      </div>
    </div>
    {c.evidence_needed && <p className="iv-small" style={{ margin: '0.2rem 0 0' }}>{c.evidence_needed}</p>}
    <input className="iv-input" style={{ marginTop: '0.4rem' }} placeholder="Evidence you heard (optional)" value={r.note || ''} onChange={(e) => onChange({ ...r, note: e.target.value })} data-testid={`scorecard-note-${i}`} />
  </div>
);

function Editor({ bookingId, v, onSaved }) {
  const [f, setF] = useState(toForm(v.mine));
  const [saved, setSaved] = useState(v.mine ? 'Draft saved' : '');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = useRef(false);
  useEffect(() => {
    if (!dirty.current) return undefined;
    setSaved('Saving…');
    const t = setTimeout(() => roomAPI.saveScorecard(bookingId, toBody(f)).then(() => setSaved('Draft saved')).catch((e) => setSaved(errMsg(e, 'Not saved'))), 1200);
    return () => clearTimeout(t);
  }, [f, bookingId]);
  const edit = (fn) => { dirty.current = true; setF(fn); };
  const scored = Object.values(f.ratings).some((r) => r.score);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await roomAPI.saveScorecard(bookingId, { ...toBody(f), submit: true });
      dirty.current = false;
      toast.success('Scorecard submitted');
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not submit'));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  return (
    <div className="vr-panel-body" data-testid="scorecard-editor">
      <div className="iv-note"><Lock size={13} /> Blind scoring: you'll see the other interviewers' scores after you submit yours.</div>
      {v.competencies.map((c, i) => <ScoreRow key={c.name} c={c} i={i} r={f.ratings[c.name] || {}} onChange={(r) => edit((x) => ({ ...x, ratings: { ...x.ratings, [c.name]: r } }))} />)}
      <textarea className="iv-textarea" rows={4} placeholder="Overall notes: strengths, concerns, what to probe next round" value={f.notes} onChange={(e) => { const val = e.target.value; edit((x) => ({ ...x, notes: val })); }} data-testid="scorecard-notes" />
      <div>
        <div className="iv-eyebrow" style={{ marginBottom: '0.4rem' }}>Your recommendation</div>
        <div className="vr-recs">
          {RECS.map(([k, l]) => <button type="button" key={k} className={`vr-rec vr-rec-${k} ${f.recommendation === k ? 'is-on' : ''}`} onClick={() => edit((x) => ({ ...x, recommendation: k }))} data-testid={`scorecard-rec-${k}`}>{l}</button>)}
        </div>
      </div>
      <div className="iv-row vr-submit-bar" style={{ justifyContent: 'space-between' }}>
        <span className="iv-small" data-testid="scorecard-save-state">{saved}</span>
        {!confirm ? (
          <button type="button" className="iv-btn iv-btn-cta" disabled={!v.can_submit || !scored || !f.recommendation} onClick={() => setConfirm(true)} data-testid="scorecard-submit-btn">Submit scorecard</button>
        ) : (
          <div className="iv-row">
            <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => setConfirm(false)} data-testid="scorecard-submit-cancel">Keep editing</button>
            <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={busy} onClick={submit} data-testid="scorecard-submit-confirm">Submit, can't be edited</button>
          </div>
        )}
      </div>
      {!v.can_submit && <p className="iv-small" data-testid="scorecard-submit-hint">You can submit once the interview has started. Your draft saves as you go.</p>}
    </div>
  );
}

export default function ScorecardPanel({ bookingId }) {
  const [v, setV] = useState(null);
  const load = useCallback(() => roomAPI.scorecards(bookingId).then(({ data }) => setV(data)).catch((e) => toast.error(errMsg(e, 'Could not load scorecards'))), [bookingId]);
  useEffect(() => { load(); }, [load]);
  if (!v) return <p className="iv-small">Loading scorecard…</p>;
  const editing = v.is_panel && v.mine?.status !== 'submitted';
  return (
    <div data-testid="scorecard-panel">
      <div className="vr-panel-body" style={{ paddingBottom: 0 }}>
        <div className="iv-row" style={{ justifyContent: 'space-between' }}>
          <span className="iv-eyebrow">Panel</span>
          <button type="button" className="vr-link" onClick={load} data-testid="scorecard-refresh-btn">Refresh</button>
        </div>
        <PanelStatus panel={v.panel} />
      </div>
      {editing ? <Editor bookingId={bookingId} v={v} onSaved={setV} /> : (
        <div className="vr-panel-body">
          {v.verdict ? <VerdictCard v={v.verdict} /> : <div className="iv-note" data-testid="scorecard-waiting">The panel verdict appears when every interviewer has submitted.</div>}
          {v.mine && <CardView card={v.mine} comps={v.competencies} />}
          {v.others.map((c) => <CardView key={c.user_id} card={c} comps={v.competencies} />)}
          {!v.is_panel && !v.others.length && <p className="iv-small">No scorecards submitted yet.</p>}
        </div>
      )}
    </div>
  );
}
